import React, { useState, useCallback } from 'react';
import { Tabs } from 'antd';
import { Box } from 'atoms';
import { useQueryParams } from 'hooks';
import {
	PendingApprovalsTab,
	PendingTripsTab,
	AllAdhocRequestsTab,
	AdhocRequestFilters,
} from './components';
import {
	fetchPendingApprovals,
	fetchPendingTrips,
	fetchAllAdhocRequests,
	fetchPendingApprovalsV2,
	fetchPendingTripsV2,
	fetchAllAdhocRequestsV2,
} from 'store/advancePaymentSlice';
import { getUIVersion } from 'utils/router-static-data';

const { TabPane } = Tabs;

const PendingApprovals = 'pending-approvals';
const PendingTrips = 'pending-trips';
const All = 'all';

function AdhocRequest() {
	const [activeTab, setActiveTab] = useState(PendingApprovals);
	const isV2UI = getUIVersion() === 'new';

	// State for each tab
	const [pendingApprovalsData, setPendingApprovalsData] = useState({ rows: [], count: 0 });
	const [pendingTripsData, setPendingTripsData] = useState({ rows: [], count: 0 });
	const [allAdhocRequestsData, setAllAdhocRequestsData] = useState({ rows: [], count: 0 });

	// Pagination config for each tab using useQueryParams
	// Note: search_type is NOT included here as it's hardcoded in the action functions
	const [pendingApprovalsPagination, setPendingApprovalsPagination] = useQueryParams({
		fetch_filters: false,
		business_unit_ids: [],
	});

	const [pendingTripsPagination, setPendingTripsPagination] = useQueryParams({
		fetch_filters: false,
		business_unit_ids: [],
	});

	const [allAdhocRequestsPagination, setAllAdhocRequestsPagination] = useQueryParams({
		fetch_filters: false,
		business_unit_ids: [],
	});

	// Helper function to handle business unit IDs for each tab
	const getBusinessUnitIds = useCallback((paginationConfig) => {
		const buIds = paginationConfig?.business_unit_ids;
		let result = [];

		if (Array.isArray(buIds)) {
			result = buIds;
		} else if (buIds) {
			result = [buIds];
		}

		return result.map((businessUnitId) => String(businessUnitId).trim()).filter(Boolean);
	}, []);

	// Dispatch actions for each tab
	const memoizedPendingApprovalsAction = useCallback(
		(params) => {
			const currentConfig = pendingApprovalsPagination;
			const filterParams = {
				limit: params.limit || 10,
				offset: params.offset || 0,
				city: currentConfig?.city,
				client_id: currentConfig?.client_id,
				project_id: currentConfig?.project_id,
				vendor_id: currentConfig?.vendor_id,
				trip_status: currentConfig?.trip_status,
				business_unit_ids: getBusinessUnitIds(currentConfig),
				from_date: currentConfig?.from_date,
				to_date: currentConfig?.to_date,
			};
			// Only include query_str if it has a valid value
			if (currentConfig?.query_str && currentConfig.query_str.trim()) {
				filterParams.query_str = currentConfig.query_str.trim();
			}
			return async (dispatchThunk) => {
				// Use V2 API for new UI, V1 for old UI
				const action = isV2UI ? fetchPendingApprovalsV2 : fetchPendingApprovals;
				return dispatchThunk(action(filterParams)).then((result) => ({
					rows: result.rows || [],
					count: result.count || 0,
				}));
			};
		},
		[pendingApprovalsPagination, getBusinessUnitIds, isV2UI]
	);

	const memoizedPendingTripsAction = useCallback(
		(params) => {
			const currentConfig = pendingTripsPagination;
			const filterParams = {
				limit: params.limit || 10,
				offset: params.offset || 0,
				city: currentConfig?.city,
				client_id: currentConfig?.client_id,
				project_id: currentConfig?.project_id,
				vendor_id: currentConfig?.vendor_id,
				trip_status: currentConfig?.trip_status,
				business_unit_ids: getBusinessUnitIds(currentConfig),
				from_date: currentConfig?.from_date,
				to_date: currentConfig?.to_date,
			};
			// Only include query_str if it has a valid value
			if (currentConfig?.query_str && currentConfig.query_str.trim()) {
				filterParams.query_str = currentConfig.query_str.trim();
			}
			return async (dispatchThunk) => {
				// Use V2 API for new UI, V1 for old UI
				const action = isV2UI ? fetchPendingTripsV2 : fetchPendingTrips;
				return dispatchThunk(action(filterParams)).then((result) => ({
					rows: result.rows || [],
					count: result.count || 0,
				}));
			};
		},
		[pendingTripsPagination, getBusinessUnitIds, isV2UI]
	);

	const memoizedAllAdhocRequestsAction = useCallback(
		(params) => {
			const currentConfig = allAdhocRequestsPagination;
			const filterParams = {
				limit: params.limit || 10,
				offset: params.offset || 0,
				city: currentConfig?.city,
				client_id: currentConfig?.client_id,
				project_id: currentConfig?.project_id,
				vendor_id: currentConfig?.vendor_id,
				trip_status: currentConfig?.trip_status,
				business_unit_ids: getBusinessUnitIds(currentConfig),
				from_date: currentConfig?.from_date,
				to_date: currentConfig?.to_date,
			};
			// Only include query_str if it has a valid value
			if (currentConfig?.query_str && currentConfig.query_str.trim()) {
				filterParams.query_str = currentConfig.query_str.trim();
			}
			return async (dispatchThunk) => {
				// Use V2 API for new UI, V1 for old UI
				const action = isV2UI ? fetchAllAdhocRequestsV2 : fetchAllAdhocRequests;
				return dispatchThunk(action(filterParams)).then((result) => ({
					rows: result.rows || [],
					count: result.count || 0,
				}));
			};
		},
		[allAdhocRequestsPagination, getBusinessUnitIds, isV2UI]
	);

	// Note: Data loading is handled by RCTable component via dispatchAction
	// RCTable calls dispatchAction when paginateConfig changes, so we don't need
	// to manually load data here. This prevents duplicate API calls.

	return (
		<Box mt={4}>
			<Tabs activeKey={activeTab} onChange={setActiveTab}>
				<TabPane tab="Pending Approvals" key={PendingApprovals}>
					<AdhocRequestFilters
						paginationConfig={pendingApprovalsPagination}
						setPaginationConfig={setPendingApprovalsPagination}
						filterKey="PENDING_APPROVALS"
						showCreateButton
					/>
					<PendingApprovalsTab
						tableDatas={[pendingApprovalsData, setPendingApprovalsData]}
						togglePaginate={[pendingApprovalsPagination, setPendingApprovalsPagination]}
						dispatchAction={memoizedPendingApprovalsAction}
					/>
				</TabPane>
				<TabPane tab="Pending Trips" key={PendingTrips}>
					<AdhocRequestFilters
						paginationConfig={pendingTripsPagination}
						setPaginationConfig={setPendingTripsPagination}
						filterKey="PENDING_TRIPS"
					/>
					<PendingTripsTab
						tableDatas={[pendingTripsData, setPendingTripsData]}
						togglePaginate={[pendingTripsPagination, setPendingTripsPagination]}
						dispatchAction={memoizedPendingTripsAction}
					/>
				</TabPane>
				<TabPane tab="All" key={All}>
					<AdhocRequestFilters
						paginationConfig={allAdhocRequestsPagination}
						setPaginationConfig={setAllAdhocRequestsPagination}
						filterKey="ALL"
					/>
					<AllAdhocRequestsTab
						tableDatas={[allAdhocRequestsData, setAllAdhocRequestsData]}
						togglePaginate={[allAdhocRequestsPagination, setAllAdhocRequestsPagination]}
						dispatchAction={memoizedAllAdhocRequestsAction}
					/>
				</TabPane>
			</Tabs>
		</Box>
	);
}

export default AdhocRequest;
